<script>
import { activeBrand } from '@/themes/primevue/brandState.js';
import DropdownMenuItem from '../Components/DropdownMenuItem.vue';
import NavLink from '../Components/NavLink.vue';
import DropdownMenuToggle from '../Components/DropdownMenuToggle.vue';
import { Link, router } from '@inertiajs/vue3';
import { markRaw } from 'vue';
import WallMessages from '../Globals/WallMessages.vue';
import Notifications from '../Globals/Notifications.vue';
import Alerts from '../Globals/Alerts.vue';
import SimpleSelect from '../Components/Inputs/SimpleSelect.vue';
import Icon from '../Components/Icons/Icon.vue';
import ChatIcon from '../Components/InternalChat/ChatIcon.vue';
import RingCentralCallerID from '../Components/InboundPhoneCall/RingCentralCallerID.vue';
import InboundCallerID from '../Components/InboundPhoneCall/InboundCallerID.vue';
import TimeClockControl from '../Components/ShopManagement/TimeClockControl.vue';
import { timeClockClient } from '../ShopManagement/timeClockClient.js';
import { setThemeFromMode, THEME_MODE } from '../Utils/theme.js';

// Below this width the header collapses into the hamburger sidebar, so the
// inline top menu is hidden and there is nothing to measure.
const INLINE_MENU_BREAKPOINT = 992;

// Width to reserve for the "More" item itself. It is not in the DOM while
// everything fits, so it cannot be measured alongside the rest.
const MORE_MENU_WIDTH = 96;

export default {
  components: {
    SimpleSelect,
    Alerts,
    Notifications,
    WallMessages,
    DropdownMenuToggle,
    DropdownMenuItem,
    NavLink,
    Link,
    Icon,
    ChatIcon,
    RingCentralCallerID,
    InboundCallerID,
    TimeClockControl,
  },

  data() {
    return {
      user: this.$page.props.auth.user,
      master_config: this.$page.props.master_config,
      top_menu: window.top_menu,
      user_menu: window.user_menu,
      wss_url: this.$page.props.wss_url,
      toggles: {
        ...this.$page.props.toggles,
        notifications_retention: this.$page.props.toggles?.notifications_retention ?? 7,
      },
      modules: this.$page.props.module,
      switch_shop: this.$page.props.switch_shops,
      hide_header: this.$page.props.hide_header ?? false,
      showUserMenu: false,
      isSidebarOpen: false,
      menuItemWidths: null,
      hiddenMenuCount: 0,
      navResizeObserver: null,

      assetVersion: null,
      versionCheckInterval: null,
      themeUpdating: false,
    };
  },

  computed: {
    productName() {
      return activeBrand.value.productName;
    },
    currentThemeMode() {
      return Number(this.user?.dark_mode ?? THEME_MODE.SYSTEM);
    },

    themeModeOptions() {
      return [
        {
          mode: THEME_MODE.LIGHT,
          icon: 'fa fa-sun',
          label: 'Light theme',
        },
        {
          mode: THEME_MODE.DARK,
          icon: 'fa fa-moon',
          label: 'Dark theme',
        },
        {
          mode: THEME_MODE.SYSTEM,
          icon: 'fa fa-desktop',
          label: 'Match system theme',
        },
      ];
    },

    redirectRouteHomePage() {
      if (this.master_config !== null) {
        return route('dashboard.index', { shop_id: this.master_config.shop_id });
      }
    },

    canUseTimeClock() {
      return timeClockClient.state.canUseClock;
    },

    // Menu entries that may move into the "More" dropdown. Components
    // (notifications) and mobile-first icons stay put: they are icon sized and
    // read as chrome rather than navigation.
    overflowCandidateIndexes() {
      return this.top_menu.reduce((indexes, menu, index) => {
        if (menu.component === undefined && menu.mobile === undefined) {
          indexes.push(index);
        }

        return indexes;
      }, []);
    },

    hiddenMenuIndexes() {
      if (this.hiddenMenuCount < 1) {
        return [];
      }

      const candidates = this.overflowCandidateIndexes;

      return candidates.slice(Math.max(candidates.length - this.hiddenMenuCount, 0));
    },

    // Flattened for the dropdown: a menu with children becomes a labelled
    // group, a plain menu becomes a single item.
    overflowSections() {
      return this.top_menu
        .filter((menu, index) => this.hiddenMenuIndexes.includes(index))
        .map((menu) => ({
          label: menu.dropdown !== undefined ? menu.name : null,
          items: (menu.dropdown !== undefined ? menu.dropdown : [menu])
            .filter((item) => item.can_access !== false)
            .map((item) => ({
              name: item.name,
              icon: item.icon || '',
              url: this.menuItemUrl(item),
              inertia: this.menuItemUsesInertia(item),
            }))
            .filter((item) => item.url),
        }))
        .filter((section) => section.items.length > 0);
    },

    sidebarLinkMenus() {
      return this.top_menu.filter(
        (menu) =>
          menu.dropdown === undefined &&
          menu.component === undefined &&
          // Icon-only entries (help, notifications) stay in the top bar; they
          // are already visible at every width and have no label to show here.
          !!menu.name &&
          !!menu.link,
      );
    },

    shopsSelections() {
      let shops = {};

      Object.keys(this.switch_shop).forEach((key) => {
        shops[this.switch_shop[key].shop_id] = this.switch_shop[key].shop_name;
      });

      return shops;
    },
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateMenuOverflow);
    this.navResizeObserver?.disconnect();

    // Clean up listeners when component is destroyed
    if (this.master_config && window.Echo) {
      // Leave shop-wide channel
      window.Echo.leave(`status.update.failure.shop.${this.master_config.shop_id}`);

      // Leave user-specific channel
      if (this.user) {
        window.Echo.leave(
          `status.update.failure.shop.${this.master_config.shop_id}.user.${this.user.id}`,
        );
      }
    }

    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
    }
  },

  mounted() {
    this.startVersionCheck();

    this.updateMenuOverflow();
    window.addEventListener('resize', this.updateMenuOverflow);
    this.$nextTick(this.observeNavResize);

    // Icon fonts land after the first paint and change every item's width, so
    // throw the cached measurements away once they are in.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        this.menuItemWidths = null;
        this.updateMenuOverflow();
      });
    }

    $('.dropdown-user-menu-toggle').on('click', function () {
      $('.dropdown-user-menu').toggleClass('show');
      $(this).toggleClass('show');
    });

    $('.dropdown-user-menu a.dropdown-item').on('click', function () {
      $('.dropdown-user-menu').toggleClass('show');
    });

    let vm = this;

    $(document).on('click', function (e) {
      // Check if the click is NOT inside .dropdown-menu-end
      if (
        !$(e.target).closest('.dropdown-menu-end').length &&
        !$(e.target).closest('.nav-link').length
      ) {
        vm.showUserMenu = false;
      }
    });

    $(document).on('click', function (e) {
      var $target = $(e.target);
      var isNavLinkInToggle = $target.closest('.dropdown-menu-end a.dropdown-item').length > 0;
      if (isNavLinkInToggle) {
        vm.showUserMenu = false;
      }
    });

    $('body').on('click', function (e) {
      let clickedElementClass = $(e.target).attr('class');
      if (
        $('.dropdown-user-menu').hasClass('show') &&
        !clickedElementClass.includes('dropdown-user-menu-toggle') &&
        !clickedElementClass.includes('dropdown-item dropdown-toggle') &&
        !clickedElementClass.includes('dropdown-toggle-avatar') &&
        !clickedElementClass.includes('dropdown-toggle-username')
      ) {
        $('.dropdown-user-menu').toggleClass('show');
      }
    });

    this.listenForStatusUpdateFailures();
  },

  methods: {
    menuItemUrl(item) {
      if (item.link) {
        return item.link;
      }

      if (item.route && this.master_config) {
        return route(item.route, {
          ...(item.parameters ?? {}),
          shop_id: this.master_config.shop_id,
        });
      }

      return null;
    },

    menuItemUsesInertia(item) {
      return (
        !!item.route &&
        !item.link &&
        item.external !== true &&
        item.blade !== true &&
        item.redirect !== true
      );
    },

    // Caches each menu item's natural width once, while they are all inline.
    // Widths are text driven and stable, which is what lets the resize path
    // stay arithmetic rather than re-measuring a list it is shrinking.
    measureMenuItems() {
      const list = this.$refs.navList;

      if (!list) {
        return false;
      }

      const widths = {};
      list.querySelectorAll('[data-top-menu-index]').forEach((item) => {
        widths[item.dataset.topMenuIndex] = item.getBoundingClientRect().width;
      });

      if (Object.keys(widths).length !== this.top_menu.length) {
        return false;
      }

      this.menuItemWidths = widths;

      return true;
    },

    // Measured fresh every pass, never cached: these are always in the row, and
    // the Time Clock control grows once it has loaded its state.
    measureStaticNavWidth() {
      const list = this.$refs.navList;

      if (!list) {
        return 0;
      }

      let width = 0;
      list
        .querySelectorAll(':scope > li:not([data-top-menu-index]):not([data-nav-more])')
        .forEach((item) => {
          width += item.getBoundingClientRect().width;
        });

      return width;
    },

    // The collapse is flex-grow, so its width tracks the viewport rather than
    // its own contents: watching it cannot feed back into itself. The static
    // items are watched so a late-loading control re-runs the calculation.
    observeNavResize() {
      if (typeof ResizeObserver === 'undefined' || !this.$refs.navCollapse) {
        return;
      }

      // markRaw: a reactive proxy around a ResizeObserver makes observe() throw
      // an illegal invocation.
      this.navResizeObserver = markRaw(new ResizeObserver(() => this.updateMenuOverflow()));
      this.navResizeObserver.observe(this.$refs.navCollapse);
      this.$refs.navList
        ?.querySelectorAll(':scope > li:not([data-top-menu-index]):not([data-nav-more])')
        .forEach((item) => this.navResizeObserver.observe(item));
    },

    async updateMenuOverflow() {
      if (this.hide_header || window.innerWidth < INLINE_MENU_BREAKPOINT) {
        this.hiddenMenuCount = 0;

        return;
      }

      if (!this.menuItemWidths) {
        if (this.hiddenMenuCount !== 0) {
          this.hiddenMenuCount = 0;
          await this.$nextTick();
        }

        if (!this.measureMenuItems()) {
          return;
        }
      }

      const available = this.$refs.navCollapse?.clientWidth ?? 0;

      if (available < 1) {
        return;
      }

      const candidates = this.overflowCandidateIndexes;
      const totalWidth = Object.values(this.menuItemWidths).reduce(
        (total, width) => total + width,
        this.measureStaticNavWidth(),
      );

      let hidden = 0;
      let width = totalWidth;

      while (width > available && hidden < candidates.length) {
        if (hidden === 0) {
          width += MORE_MENU_WIDTH;
        }

        width -= this.menuItemWidths[candidates[candidates.length - 1 - hidden]] ?? 0;
        hidden += 1;
      }

      this.hiddenMenuCount = hidden;
    },

    toggleSidebar() {
      const sidebar = document.querySelector('nav#sidebar');
      if (!sidebar) return;
      this.isSidebarOpen = sidebar.classList.toggle('collapsed');
    },

    setVersionCookie(version) {
      document.cookie = `asset_version=${version}; path=/; max-age=86400`; // 24hr
    },

    getVersionCookie() {
      const match = document.cookie.match(/asset_version=([^;]+)/);
      return match ? match[1] : null;
    },

    async startVersionCheck() {
      try {
        const res = await fetch(route('build.version') + '?t=' + Date.now());
        if (!res.ok) return;

        const data = await res.json();
        if (!data?.version) return;

        this.assetVersion = data.version;

        const cookieVersion = this.getVersionCookie();
        if (cookieVersion && cookieVersion !== this.assetVersion) {
          this.setVersionCookie(this.assetVersion);
          window.location.href = window.location.href.split('?')[0] + '?v=' + this.assetVersion;
          return;
        }

        this.setVersionCookie(this.assetVersion);
      } catch (e) {
        return; // silently fail - don't break the app
      }

      this.versionCheckInterval = setInterval(
        async () => {
          try {
            const res = await fetch(route('build.version') + '?t=' + Date.now());
            if (!res.ok) return;

            const data = await res.json();
            if (!data?.version) return;

            const newVersion = data.version;

            if (this.assetVersion && newVersion !== this.assetVersion) {
              clearInterval(this.versionCheckInterval);
              this.setVersionCookie(newVersion);

              window._forceReload = () => {
                window.location.href = window.location.href.split('?')[0] + '?v=' + newVersion;
              };

              if (window.notyf) {
                const notification = window.notyf.open({
                  type: 'success',
                  message: '🔄 A new version is available. Click here to update.',
                  duration: 0,
                  dismissible: true,
                });

                notification.on('click', () => {
                  window.location.href = window.location.href.split('?')[0] + '?v=' + newVersion;
                });
              }
            }
          } catch (e) {
            // silently fail - try again next interval
          }
        },
        2 * 60 * 1000,
      );
    },

    listenForStatusUpdateFailures() {
      if (!this.master_config || !window.Echo) {
        return;
      }

      const shopId = this.master_config.shop_id;
      const userId = this.user?.id;

      // Listen to shop-wide failures
      window.Echo.private(`status.update.failure.shop.${shopId}`).listen(
        '.status.update.failed',
        (event) => {
          console.error('Status update failed:', event);
          this.showStatusUpdateError(event);
        },
      );

      // Listen to user-specific failures (if user exists)
      if (userId) {
        window.Echo.private(`status.update.failure.shop.${shopId}.user.${userId}`).listen(
          '.status.update.failed',
          (event) => {
            console.error('Status update failed for user:', event);
            this.showStatusUpdateError(event);
          },
        );
      }
    },

    showStatusUpdateError(event) {
      const message =
        event.message || 'There was an error updating the status. Please try again later.';

      if (window.notyf) {
        window.notyf.error(message);
      } else {
        // Fallback if notyf is not available
        console.error('Status update error:', message, event);
        alert(message);
      }
    },

    uniqueIDValue(value, prefix) {
      return prefix + '_' + value.replace(/\W/g, '_');
    },

    changeShops(shop) {
      window.location = route('dashboard.select-shop', { shop_id: shop });
    },

    updateThemeMode(mode) {
      if (this.themeUpdating || this.currentThemeMode === mode) {
        return;
      }

      const previousMode = this.currentThemeMode;
      this.themeUpdating = true;
      this.user.dark_mode = mode;
      this.$page.props.auth.user.dark_mode = mode;
      setThemeFromMode(mode);

      if (!this.master_config) {
        this.themeUpdating = false;
        return;
      }

      axios
        .post(
          route('user.theme', {
            shop_id: this.master_config.shop_id,
          }),
          { dark_mode: mode },
        )
        .catch(() => {
          this.user.dark_mode = previousMode;
          this.$page.props.auth.user.dark_mode = previousMode;
          setThemeFromMode(previousMode);
          notyf.error('Unable to update theme preference.');
        })
        .finally(() => {
          this.themeUpdating = false;
        });
    },
  },
};
</script>

<template>
  <div class="wrapper">
    <nav id="sidebar" class="sidebar js-sidebar d-xl-none d-lg-none">
      <div class="sidebar-content js-simplebar">
        <ul class="sidebar-nav">
          <li class="sidebar-header">
            <p class="shop-title">
              {{ master_config?.shop_name }}
            </p>
          </li>

          <hr />

          <!-- Flat entries. The old filter was `external === true`, which no
               shop's menu satisfies, so every link without a submenu was
               missing from the sidebar and rendered an empty <li>. -->
          <li v-for="menu in sidebarLinkMenus" :key="menu.name" class="sidebar-item">
            <a class="sidebar-link" :href="menu.link">
              <i :class="menu.icon" />
              <span class="align-middle">{{ menu.name }}</span>
            </a>
          </li>

          <li
            v-for="menu in top_menu.filter((menu) => menu.dropdown !== undefined)"
            :key="menu.name"
            class="sidebar-item"
          >
            <a
              :data-bs-target="'#' + uniqueIDValue(menu.name, 'sidebar')"
              data-bs-toggle="collapse"
              class="sidebar-link collapsed"
            >
              <i :class="menu.icon" />
              <span class="align-middle">{{ menu.name }}</span>
            </a>
            <ul
              :id="uniqueIDValue(menu.name, 'sidebar')"
              class="sidebar-dropdown list-unstyled collapse"
            >
              <li v-for="dropDownItem in menu.dropdown" class="sidebar-item">
                <DropdownMenuItem
                  :name="dropDownItem.name"
                  :icon="dropDownItem.icon"
                  :link="dropDownItem.link"
                  :letter="dropDownItem.letter"
                  :route="dropDownItem.route"
                  :parameters="dropDownItem.parameters"
                />
              </li>
            </ul>
          </li>
        </ul>

        <div class="sidebar-cta" />
      </div>
    </nav>

    <div class="main">
      <nav v-if="!hide_header" class="navbar navbar-expand navbar-light navbar-bg">
        <!-- Vue owns this click; the legacy js-sidebar-toggle hook would toggle the sidebar twice. -->
        <button
          v-if="master_config !== null"
          type="button"
          class="sidebar-toggle d-xl-none d-lg-none"
          aria-label="Toggle navigation menu"
          aria-controls="sidebar"
          :aria-expanded="isSidebarOpen"
          @click="toggleSidebar"
        >
          <i class="hamburger align-self-center" />
        </button>

        <template v-if="master_config !== null">
          <a
            :href="route('active-customers-dashboard.index', { shop_id: master_config?.shop_id })"
            class="d-inline d-lg-none link-dark"
            id="topbar"
          >
            <span class="d-none d-sm-block" id="topbar-name">
              {{ master_config?.shop_name }}
            </span>
            <span class="d-block d-sm-none" id="topbar-icon">
              <i class="fa fa-home" />
            </span>
          </a>
        </template>
        <div class="shop-title d-none d-lg-inline">
          <a
            v-if="master_config !== null"
            :href="route('active-customers-dashboard.index', { shop_id: master_config.shop_id })"
            class="link-dark text-decoration-none"
          >
            <p class="mb-0" v-html="master_config.shop_name" />
          </a>
          <p v-else class="mb-0">{{ productName }}</p>
          <div v-if="master_config !== null" class="desktop-qr-section">
            <div>
              <span class="powered-by me-2">by {{ productName }}</span>
            </div>
          </div>
        </div>

        <div ref="navCollapse" class="navbar-collapse collapse">
          <ul ref="navList" class="navbar-nav navbar-align">
            <template v-for="(menu, index) in top_menu" :key="menu.name + '-' + index">
              <li
                v-if="!hiddenMenuIndexes.includes(index)"
                class="nav-item nav-item-top-menu"
                :class="menu?.dropdown?.length > 0 ? 'dropdown' : ''"
                :data-top-menu-index="index"
              >
                <!-- For blade files, use regular <a> tag instead of Inertia Link -->
                <a
                  v-if="
                    menu.blade === true &&
                    menu.component === undefined &&
                    menu.dropdown === undefined
                  "
                  :href="route(menu.route, { shop_id: master_config.shop_id })"
                  class="nav-link d-none d-lg-block"
                >
                  <i v-if="menu.icon" :class="menu.icon" />
                  <span>{{ menu.name }}</span>
                </a>

                <!-- For dropdown menus with blade items -->
                <DropdownMenuToggle
                  v-else-if="menu.dropdown !== undefined"
                  class="d-none d-lg-block"
                  :name="menu.name"
                  :icon="menu.icon"
                />

                <!-- For other menu types (Inertia routes) -->
                <Notifications
                  v-else-if="menu.component !== undefined && menu.component == 'notifications'"
                />

                <div v-if="menu.dropdown !== undefined" class="dropdown-menu dropdown-menu-end">
                  <DropdownMenuItem
                    v-for="dropdown_item in menu.dropdown"
                    class="d-none d-lg-block"
                    :show="dropdown_item.can_access"
                    :name="dropdown_item.name"
                    :link="dropdown_item.link"
                    :route="dropdown_item.route"
                    :parameters="dropdown_item?.parameters"
                    :component="dropdown_item?.component"
                    :type="dropdown_item?.type"
                    :letter="dropdown_item?.letter"
                    :icon="dropdown_item?.icon"
                    :redirect="dropdown_item?.redirect"
                    :blade="dropdown_item?.blade"
                  />
                </div>

                <NavLink
                  v-else-if="menu.component === undefined"
                  :name="menu.name"
                  :class="menu.mobile === undefined ? 'd-none d-lg-block' : ''"
                  :link="menu.link"
                  :icon="menu?.icon"
                  :blade="menu?.blade"
                />
              </li>
            </template>

            <!-- Whatever the header could not fit on one row. Rendered flat so
                 a menu's children are one click away instead of two. -->
            <li
              v-if="overflowSections.length > 0"
              data-nav-more
              class="nav-item nav-item-top-menu dropdown d-none d-lg-block"
            >
              <a class="nav-link dropdown-toggle" href="#" data-bs-toggle="dropdown" title="More">
                <i class="fa fa-ellipsis me-1" aria-hidden="true" />
                <span>More</span>
              </a>
              <div class="dropdown-menu dropdown-menu-end top-menu-overflow">
                <template v-for="(section, sectionIndex) in overflowSections" :key="sectionIndex">
                  <h6 v-if="section.label" class="dropdown-header">{{ section.label }}</h6>
                  <template v-for="item in section.items" :key="item.name">
                    <Link v-if="item.inertia" class="dropdown-item" :href="item.url">
                      <i v-if="item.icon" :class="item.icon" />
                      {{ item.name }}
                    </Link>
                    <a v-else class="dropdown-item" :href="item.url">
                      <i v-if="item.icon" :class="item.icon" />
                      {{ item.name }}
                    </a>
                  </template>
                </template>
              </div>
            </li>

            <li v-if="switch_shop" class="nav-item">
              <a class="nav-link" :href="route('dashboard.index')">
                <Icon class="fa-solid fa-shop" tooltip="Switch Shops" />
              </a>
            </li>
            <!-- Beside the profile menu: the clock, then My Time (the employee's own
                 entries, manual entries, and correction requests). Both only for an
                 employee who has Time Clock enabled. -->
            <li v-if="$page.props.showTimeClock" class="nav-item">
              <div class="time-clock-nav d-flex align-items-center">
                <TimeClockControl :user="user" />
                <Link
                  v-if="master_config && canUseTimeClock"
                  :href="route('time-clock.my-time', { shop_id: master_config.shop_id })"
                  class="nav-link px-2"
                  title="My Time"
                  aria-label="My Time"
                >
                  <i class="fa-solid fa-clock-rotate-left" aria-hidden="true" />
                </Link>
              </div>
            </li>
            <li class="nav-item">
              <a v-on:click="showUserMenu = showUserMenu === false" class="nav-link" href="#">
                <img v-if="user.avatar" :src="user.avatar" class="avatar rounded me-1" />
                <i v-else class="fa fa-user fa-admin avatar dropdown-toggle-avatar img-fluid" />

                <span v-if="master_config" class="d-none d-xl-inline dropdown-toggle-username">{{
                  user.full_name
                }}</span>
                <span v-else class="d-none d-xl-inline dropdown-toggle-username">{{
                  user.email != null ? user.email : user.cell_number
                }}</span>
              </a>
              <div class="dropdown-menu dropdown-menu-end" :class="showUserMenu ? 'show' : ''">
                <DropdownMenuItem
                  v-for="menu_item in user_menu"
                  :route="menu_item.route"
                  :parameters="menu_item?.parameters"
                  :name="menu_item.name"
                  :link="menu_item.link"
                  :component="menu_item?.component"
                  :type="menu_item?.type"
                  :letter="menu_item?.letter"
                  :icon="menu_item?.icon"
                  :dropdown="menu_item.dropdown !== undefined"
                  :drop-down-items="menu_item.dropdown"
                  :redirect="menu_item?.redirect"
                />
                <div class="user-theme-switcher-row" role="group" aria-label="Theme mode">
                  <span class="user-theme-switcher-label">Theme</span>
                  <button
                    v-for="option in themeModeOptions"
                    :key="option.mode"
                    type="button"
                    class="user-theme-switcher-button"
                    :class="{ active: currentThemeMode === option.mode }"
                    :aria-label="option.label"
                    :aria-pressed="currentThemeMode === option.mode"
                    :title="option.label"
                    :disabled="themeUpdating"
                    @click.stop.prevent="updateThemeMode(option.mode)"
                  >
                    <i :class="option.icon" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </nav>

      <main :class="hide_header ? 'content no-padding' : 'content'">
        <div class="container-fluid p-0">
          <Alerts v-if="master_config !== null && hide_header === false" />

          <WallMessages v-if="master_config !== null && hide_header === false" />

          <slot />
        </div>
      </main>
    </div>
  </div>
  <div
    v-if="$page.props.appVersion && $page.props.appVersion !== 'unknown'"
    class="app-build-version"
  >
    {{ $page.props.appVersion }}
  </div>
  <ChatIcon
    v-if="!hide_header && toggles && toggles.internalchat && user && user.level_access <= 4"
  />

  <!-- INBOUND CALLER ID-->
  <RingCentralCallerID
    v-if="
      toggles &&
      toggles.phone_provider == 'ring_central' &&
      user &&
      user.show_phone_provider_caller_id
    "
  />
  <InboundCallerID
    v-if="
      user &&
      user.show_phone_provider_caller_id &&
      toggles &&
      (toggles.phone_provider == 'inbound' || toggles.phone_provider == 'goto')
    "
    :provider="toggles && toggles.phone_provider == 'inbound' ? 'inbound' : 'goto'"
    :settings="this.master_config.integration"
  />
</template>

<style scoped>
/* Same box as a neighbouring text nav-link (padding + one line of text), so the
   clock button and My Time icon centre on the same line as the other nav items
   instead of sitting a few pixels low. */
.time-clock-nav {
  min-height: calc(var(--bs-nav-link-padding-y, 0.5rem) * 2 + 1lh);
}

.time-clock-nav .nav-link {
  padding-block: 0;
}

.sidebar-toggle {
  position: relative;
  z-index: 500;
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
}

@media (min-width: 992px) {
  .content {
    margin-top: -30px;
  }
}

.content.no-padding {
  margin: 0px;
  padding: 0px;
}

.app-build-version {
  position: fixed;
  bottom: 4px;
  right: 8px;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.25);
  pointer-events: none;
  z-index: 1;
  font-family: monospace;
}

.user-theme-switcher-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding: 6px 8px;
  border-top: 1px solid var(--bs-border-color);
}

.user-theme-switcher-label {
  margin-right: auto;
  color: var(--bs-body-color);
  font-size: 0.875rem;
}

.user-theme-switcher-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 30px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  color: var(--bs-secondary-color);
  background: transparent;
  line-height: 1;
}

.user-theme-switcher-button:hover,
.user-theme-switcher-button.active {
  color: var(--bs-primary);
  background: var(--bs-tertiary-bg);
  border-color: var(--bs-border-color);
}

.user-theme-switcher-button:disabled {
  cursor: default;
  opacity: 0.65;
}
</style>
